// web/src/routes/riders/Detail.jsx
// Rider profile. Source-of-truth rule: if this rider is LINKED to an owner
// account, their contact is shown read-only from the owner (edit it on the
// owner's profile); if UNLINKED, contact is editable here. Name is always
// editable. Schedule staff only; the owner-link picker is owner/manager only
// (it reads the owner directory, which is owner/manager-gated).

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { IconButton, TrashIcon } from '../../components/ui/icons';
import { formatPhoneInput, isCompletePhone, displayPhone } from '../../lib/phone';
import { fmtDate } from '../../lib/dates';

const SCHED = ['owner', 'manager', 'trainer'];

export default function RiderDetail() {
  const { id } = useParams();
  const { activeRole } = useAuth();
  const canSchedule = SCHED.includes(activeRole);
  const canManage = activeRole === 'owner' || activeRole === 'manager';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['person', id],
    queryFn: () => api.get(`/people/${id}`),
    enabled: canSchedule,
  });

  if (!canSchedule) {
    return <div className="p-4 text-ink-500">Rider profiles are for barn staff.</div>;
  }
  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError || !data?.person) {
    return (
      <div className="p-4">
        <p className="text-red-600">Rider not found.</p>
        <Link to="/riders" className="text-ink-800 underline">Back to riders</Link>
      </div>
    );
  }

  const p = data.person;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <Link to="/riders" className="text-sm text-ink-500 hover:text-ink-800">← Riders</Link>
      <h1 className="mt-2 mb-6 text-2xl font-semibold">{p.name}</h1>

      <ContactCard riderId={id} person={p} linkedOwner={data.linkedOwner} canManage={canManage} />
      <AwayCard riderId={id} name={p.name} />
    </div>
  );
}

function ContactCard({ riderId, person, linkedOwner, canManage }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const linked = !!linkedOwner;

  const [name, setName] = useState(person.name ?? '');
  const [email, setEmail] = useState(person.email ?? '');
  const [phone, setPhone] = useState(person.phone ? formatPhoneInput(person.phone) : '');
  const [birthday, setBirthday] = useState(person.birthday ?? '');
  const [ownerToLink, setOwnerToLink] = useState('');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['person', riderId] });
    qc.invalidateQueries({ queryKey: ['people'] });
    qc.invalidateQueries({ queryKey: ['owners'] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/people/${riderId}`, linked
        ? { name: name.trim() }
        : {
            name: name.trim(),
            email: email.trim() || null,
            phone: phone.trim() || null,
            birthday: birthday || null,
          }),
    onSuccess: invalidate,
  });

  // Owner directory for the link picker (owner/manager-gated endpoint).
  const ownersQ = useQuery({
    queryKey: ['owners'],
    queryFn: () => api.get('/owners'),
    enabled: canManage && !linked,
  });

  const link = useMutation({
    mutationFn: (userId) => api.patch(`/people/${riderId}`, { userId }),
    onSuccess: () => { setOwnerToLink(''); invalidate(); },
  });

  const unlink = useMutation({
    mutationFn: () => api.patch(`/people/${riderId}`, { userId: null }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/people/${riderId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      navigate('/riders');
    },
  });

  const phoneOk = phone === '' || isCompletePhone(phone);
  const canSave = name.trim() && (linked || phoneOk) && !save.isPending;

  return (
    <>
      <section className="card p-5 space-y-4">
        <h2 className="font-medium">Contact</h2>

        <Field label="Name" htmlFor="name">
          <input id="name" className="input w-full" value={name} maxLength={120}
            onChange={(e) => setName(e.target.value)} />
        </Field>

        {linked ? (
          <div className="rounded-lg bg-ink-50 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-600">
                Linked to owner{' '}
                <Link to={`/owners/${linkedOwner.userId}`} className="font-medium text-ink-900 hover:underline">
                  {linkedOwner.name || linkedOwner.email}
                </Link>
              </span>
              <button
                onClick={() => {
                  if (confirm('Unlink from this owner? The rider keeps their own contact from here on.')) {
                    unlink.mutate();
                  }
                }}
                disabled={unlink.isPending}
                className="shrink-0 text-xs text-ink-500 hover:text-red-600"
              >
                Unlink
              </button>
            </div>
            <dl className="mt-3 space-y-1">
              <Line label="Email" value={linkedOwner.email} />
              <Line label="Phone" value={linkedOwner.phone ? displayPhone(linkedOwner.phone) : null} />
              <Line label="Birthday" value={formatBirthday(linkedOwner.birthday)} />
            </dl>
            <p className="mt-2 text-xs text-ink-400">Contact is managed on the owner’s profile.</p>
          </div>
        ) : (
          <>
            <Field label="Email" optional htmlFor="email">
              <input id="email" type="email" className="input w-full" value={email}
                onChange={(e) => setEmail(e.target.value)} />
            </Field>

            <Field label="Mobile" optional htmlFor="phone">
              <input id="phone" type="tel" inputMode="numeric" className="input w-full" value={phone}
                placeholder="(555) 555-5555"
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))} />
              {!phoneOk && <p className="mt-1 text-xs text-amber-600">Enter a 10-digit phone number.</p>}
            </Field>

            <Field label="Birthday" optional htmlFor="birthday">
              <input id="birthday" type="date" className="input w-full" value={birthday}
                onChange={(e) => setBirthday(e.target.value)} />
            </Field>
          </>
        )}

        {save.isError && <p className="text-sm text-red-600">{friendlyError(save.error)}</p>}
        {save.isSuccess && !save.isPending && <p className="text-sm text-emerald-600">Saved.</p>}

        <div className="flex items-center gap-3">
          <button className="btn-primary" disabled={!canSave} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => {
              if (confirm(`Remove ${person.name} from the roster? Their past lessons are kept.`)) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove from roster
          </button>
        </div>
      </section>

      {!linked && canManage && (
        <section className="card p-5 mt-4">
          <h2 className="font-medium mb-2">Link to an owner</h2>
          <p className="text-sm text-ink-500 mb-3">
            If this rider is also an owner, link them so their contact lives in one place.
          </p>
          <div className="flex items-center gap-2">
            <select
              className="input flex-1"
              value={ownerToLink}
              onChange={(e) => setOwnerToLink(e.target.value)}
            >
              <option value="">Choose an owner…</option>
              {(ownersQ.data?.owners ?? []).map((o) => (
                <option key={o.userId} value={o.userId}>{o.name || o.email}</option>
              ))}
            </select>
            <button
              className="btn-primary"
              disabled={!ownerToLink || link.isPending}
              onClick={() => link.mutate(ownerToLink)}
            >
              {link.isPending ? 'Linking…' : 'Link'}
            </button>
          </div>
          {link.isError && <p className="mt-2 text-sm text-red-600">{linkError(link.error)}</p>}
        </section>
      )}
    </>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-700 mb-1">
        {label}
        {optional && <span className="font-normal text-ink-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-400">{label}</dt>
      <dd className="text-ink-700">{value || '—'}</dd>
    </div>
  );
}

// Format a stored ISO birthday (YYYY-MM-DD) as "June 15" — parses directly to
// avoid Date() timezone shifts.
function formatBirthday(birthday) {
  if (!birthday) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday);
  if (!m) return null;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  if (monthIdx < 0 || monthIdx > 11 || !day) return null;
  return `${MONTHS[monthIdx]} ${day}`;
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'missing_name':
      return 'Name is required.';
    case 'no_fields':
      return 'Nothing to save.';
    case 'forbidden':
      return 'Only schedule staff can edit riders.';
    case 'not_found':
      return 'That rider no longer exists.';
    default:
      return 'Could not save. Try again.';
  }
}

function linkError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'already_rider':
      return 'That owner is already linked to a rider.';
    case 'user_not_in_barn':
      return 'That owner isn’t a member of this barn.';
    default:
      return 'Could not link. Try again.';
  }
}

/**
 * When this rider isn't around.
 *
 * It used to live on the trainer's availability page next to their own time
 * off, which put two unrelated things under one heading: a trainer being away
 * removes slots, a rider being away means don't book them. Worse, the rider
 * half wrote rows that nothing read — somebody could be at college until
 * January and still turn up on next Tuesday's schedule.
 *
 * Now it sits on the rider, where "Ada is at college" belongs, and the booking
 * guard actually reads it.
 */
function AwayCard({ riderId, name }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  const awayQ = useQuery({
    queryKey: ['time-off', 'person', riderId],
    queryFn: () => api.get(`/time-off?personId=${riderId}`),
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['time-off'] });
    qc.invalidateQueries({ queryKey: ['slots'] });
    qc.invalidateQueries({ queryKey: ['availability-day'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.post('/time-off', {
        personId: riderId,
        startDate: from,
        endDate: to || from,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      setAdding(false);
      setFrom(''); setTo(''); setReason(''); setErr('');
      refresh();
    },
    onError: (e) => setErr(awayError(e)),
  });

  const remove = useMutation({
    mutationFn: (entryId) => api.del(`/time-off/${entryId}`),
    onSuccess: refresh,
    onError: (e) => setErr(awayError(e)),
  });

  const entries = awayQ.data?.timeOff ?? [];

  return (
    <section className="card p-5 mt-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="font-medium">Away</h2>
          <p className="text-sm text-ink-500">
            {name} can&apos;t be booked on these dates, by them or by you.
          </p>
        </div>
        {!adding && (
          <button type="button" className="btn-secondary text-sm shrink-0" onClick={() => setAdding(true)}>
            Add Dates
          </button>
        )}
      </div>

      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}

      {adding && (
        <div className="rounded-lg border border-ink-200 p-3 mb-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-500 mb-1" htmlFor="away-from">From</label>
              <input
                id="away-from"
                type="date"
                className="input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-ink-500 mb-1" htmlFor="away-to">
                To <span className="text-ink-400">(same day if blank)</span>
              </label>
              <input
                id="away-to"
                type="date"
                className="input"
                min={from || undefined}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>
          <input
            className="input"
            placeholder="Reason (optional) — e.g. College term"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={!from || add.isPending}
              onClick={() => { setErr(''); add.mutate(); }}
            >
              {add.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => { setAdding(false); setErr(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {awayQ.isLoading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-ink-400">Nothing booked out.</p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {entries.map((e) => (
            <li key={e.id} className="py-2 flex items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-sm">
                  {fmtAway(e.startDate)}
                  {e.endDate !== e.startDate && ` – ${fmtAway(e.endDate)}`}
                </span>
                {e.reason && <span className="block text-xs text-ink-400">{e.reason}</span>}
              </span>
              <IconButton
                icon={TrashIcon}
                tone="danger"
                label="Remove these dates"
                disabled={remove.isPending}
                onClick={() => remove.mutate(e.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function fmtAway(iso) {
  if (!iso) return '';
  return fmtDate(iso, 'medium');
}

function awayError(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'end_before_start':
      return 'The end date has to come after the start.';
    case 'invalid_date':
      return 'Check the dates.';
    case 'rider_not_found':
      return 'That rider no longer exists.';
    // Everything the switch above doesn't name. It used to land here too, as
    // "Try again", which was advice rather than information and was wrong as
    // often as not — retrying a 404 forever will not fix a 404. The code and
    // the status are the only things that distinguish one unknown failure
    // from another, so they are what gets said.
    default: {
      const code = e?.body?.error || e?.message;
      const status = e?.status;
      if (!code && !status) return 'No response from the server. Check your connection.';
      return `Couldn’t save that${code ? `: ${code}` : ''}${status ? ` (${status})` : ''}.`;
    }
  }
}
